/*
 * Collection "Load More" — progressive enhancement over crawlable pagination.
 *
 * Works on any collection grid that renders snippets/load-more.liquid (which outputs a real
 * <a data-load-more href="...?page=N"> inside [data-load-more-wrap]) and marks its grid
 * container with [data-collection-container]. With no JS the link is an ordinary next-page link
 * and crawlers follow the page->page chain.
 *
 * Supports both card systems in this theme:
 *   - the <product-grid-item> custom element (colors/discounted templates) which self-initialises
 *     on insertion, and
 *   - the legacy .grid-product card (default collection template).
 *
 * Notes:
 *  - We fetch the button's own href (paginate.next.url, which already carries active filters +
 *    sort + page) rather than theme.AjaxRenderer.renderPage(), whose getUpdatedParams() would
 *    drop active filter params on a page-only change.
 *  - We do NOT mutate the browser URL with ?page=N (that renders only a single 24-item slice on
 *    reload); the accumulated list + scroll are restored from sessionStorage instead.
 *  - product-grid-item2 emits a <script> per card, so we append only element nodes and skip
 *    <script>/<style>/<link> (re-running them would re-call customElements.define and throw).
 */
(function () {
  'use strict';

  var SEL = {
    wrap: '[data-load-more-wrap]',
    container: '[data-collection-container]',
    button: '[data-load-more]',
    status: '[data-load-more-status]',
    // Qualified with [data-total]: the filter toolbar above the grid carries its own
    // [data-result-count] hook, and a bare selector matched that one first and bailed on its
    // missing total — leaving the real counter below the grid frozen at the first page.
    resultCount: '[data-result-count][data-total]',
    rcRange: '[data-rc-range]',
    rcFill: '[data-rc-fill]',
    card: 'product-grid-item, .grid-product',
    productLink: 'a[href*="/products/"]'
  };

  var DESKTOP_MEDIA = '(min-width: 769px)';
  var DESKTOP_TARGET = 96;
  var SOFT_CAP = 200;
  var PAGE_SIZE = 24;
  var SS_KEY = 'patina:loadmore:last';
  var STALE_MS = 30 * 60 * 1000;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function collectionKey() {
    return window.location.pathname + window.location.search;
  }

  function LoadMore(wrap) {
    this.wrap = wrap;
    this.sectionId = wrap.getAttribute('data-section-id') || '';
    this.container = document.querySelector(SEL.container);
    this.cache = {};
    this.loading = false;
    this.pagesLoaded = 1;
    if (!this.container) return;
    this.bindProductClicks();
    if (!this.restore()) this.prefillDesktop();
  }

  LoadMore.prototype.button = function () {
    return document.querySelector(SEL.button);
  };

  LoadMore.prototype.shown = function () {
    return this.container.querySelectorAll(SEL.card).length;
  };

  LoadMore.prototype.bindProductClicks = function () {
    var self = this;
    this.container.addEventListener('click', function (e) {
      var link = e.target.closest ? e.target.closest(SEL.productLink) : null;
      if (!link || !self.container.contains(link)) return;
      self.saveState(link.getAttribute('href'));
    });
  };

  LoadMore.prototype.saveState = function (href) {
    try {
      sessionStorage.setItem(SS_KEY, JSON.stringify({
        key: collectionKey(),
        pages: this.pagesLoaded,
        y: window.pageYOffset || document.documentElement.scrollTop || 0,
        href: href || '',
        t: Date.now()
      }));
    } catch (e) {}
  };

  LoadMore.prototype.restore = function () {
    var raw;
    try { raw = sessionStorage.getItem(SS_KEY); } catch (e) { return false; }
    if (!raw) return false;
    try { sessionStorage.removeItem(SS_KEY); } catch (e) {}
    var s;
    try { s = JSON.parse(raw); } catch (e) { return false; }
    if (!s || s.key !== collectionKey()) return false;
    if (s.t && (Date.now() - s.t) > STALE_MS) return false;
    if ((s.pages || 1) <= 1) return false;

    var self = this;
    var target = Math.min(Math.max(s.pages || 1, 1), Math.ceil(SOFT_CAP / PAGE_SIZE));

    function step() {
      if (self.pagesLoaded >= target || !self.button() || self.shown() >= SOFT_CAP) {
        self.scrollToSaved(s);
        return;
      }
      self.loadNext().then(function (ok) { ok ? step() : self.scrollToSaved(s); });
    }
    step();
    return true;
  };

  LoadMore.prototype.scrollToSaved = function (s) {
    var self = this;
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        var el = null;
        if (s.href) {
          var path = s.href.split('?')[0];
          var links = self.container.querySelectorAll(SEL.productLink);
          for (var i = 0; i < links.length; i++) {
            if (links[i].getAttribute('href').split('?')[0] === path) { el = links[i]; break; }
          }
        }
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ block: 'center' });
        } else if (typeof s.y === 'number') {
          window.scrollTo(0, s.y);
        }
      });
    });
  };

  LoadMore.prototype.loadNext = function () {
    var self = this;
    if (this.loading) return Promise.resolve(false);
    var btn = this.button();
    if (!btn) return Promise.resolve(false);
    var href = btn.getAttribute('href');
    if (!href) return Promise.resolve(false);

    this.loading = true;
    btn.setAttribute('aria-busy', 'true');

    return this.fetchDoc(href)
      .then(function (doc) {
        self.append(doc);
        self.advance(doc);
        self.pagesLoaded += 1;
        self.updateCount();
        self.reinit();
        self.announce();
        return true;
      })
      .catch(function () {
        return false; // leave the <a href> intact as a plain next-page link
      })
      .then(function (ok) {
        self.loading = false;
        var b = self.button();
        if (b) b.removeAttribute('aria-busy');
        return ok;
      });
  };

  LoadMore.prototype.fetchDoc = function (href) {
    if (this.cache[href]) return Promise.resolve(this.cache[href]);
    var self = this;
    var u;
    try { u = new URL(href, window.location.origin); } catch (e) { return Promise.reject(e); }
    if (this.sectionId) u.searchParams.set('section_id', this.sectionId);
    return fetch(u.toString(), { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        self.cache[href] = doc;
        return doc;
      });
  };

  LoadMore.prototype.append = function (doc) {
    var src = doc.querySelector(SEL.container);
    if (!src) return;
    var frag = document.createDocumentFragment();
    var kids = Array.prototype.slice.call(src.children);
    for (var i = 0; i < kids.length; i++) {
      var n = kids[i];
      if (n.nodeType !== 1) continue;
      var tag = n.tagName;
      if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'LINK' || tag === 'TEMPLATE') continue;
      frag.appendChild(n); // moving a parsed custom element into the live DOM upgrades + self-inits it
    }
    this.container.appendChild(frag);
  };

  LoadMore.prototype.advance = function (doc) {
    var btn = this.button();
    if (!btn) return;
    var next = doc.querySelector(SEL.button);
    var nextHref = next ? next.getAttribute('href') : null;
    if (nextHref) {
      btn.setAttribute('href', nextHref);
    } else if (btn.parentNode) {
      btn.parentNode.removeChild(btn); // reached the last page
    }
  };

  LoadMore.prototype.updateCount = function () {
    var rc = document.querySelector(SEL.resultCount);
    if (!rc) return;
    var total = parseInt(rc.getAttribute('data-total'), 10);
    if (!total) return;
    var shown = Math.min(this.shown(), total);
    var range = rc.querySelector(SEL.rcRange);
    if (range) range.textContent = '1–' + shown;
    var fill = rc.querySelector(SEL.rcFill);
    if (fill) fill.style.width = Math.min(100, Math.round(shown / total * 100)) + '%';
  };

  LoadMore.prototype.reinit = function () {
    // <product-grid-item> custom elements self-init on insertion. For the legacy .grid-product
    // grid, mirror the theme's own post-AJAX reinit (idempotent; a no-op where absent).
    try { if (window.theme && theme.sections && theme.sections.reinit) theme.sections.reinit('collection-grid'); } catch (e) {}
    try { if (window.theme && theme.reinitProductGridItem) theme.reinitProductGridItem(this.container); } catch (e) {}
    try { if (window.theme && theme.QuickShopScreens && theme.QuickShopScreens.reInit) theme.QuickShopScreens.reInit(this.container); } catch (e) {}
    try {
      if (typeof window.checkViewportForProducts === 'function') {
        window.checkViewportForProducts(document.querySelectorAll('div.grid-product'), window.collectionName);
      }
    } catch (e) {}
  };

  LoadMore.prototype.announce = function () {
    var status = document.querySelector(SEL.status);
    var label = document.querySelector('.collection-result-count__label');
    if (status && label) status.textContent = label.textContent;
  };

  // Desktop shows a fuller view (Baymard ~100-150) by topping up the mobile-lean initial page.
  // The button is hidden while topping up so it never appears-then-vanishes; it is revealed only
  // if products remain beyond the target. Mobile keeps the plain initial page + button.
  LoadMore.prototype.prefillDesktop = function () {
    if (!window.matchMedia || !window.matchMedia(DESKTOP_MEDIA).matches) return;
    var btn = this.button();
    if (!btn || this.shown() >= DESKTOP_TARGET) return;
    var self = this;
    btn.style.display = 'none';
    function reveal() { var b = self.button(); if (b) b.style.display = ''; }
    function step() {
      if (!self.button() || self.shown() >= DESKTOP_TARGET || self.shown() >= SOFT_CAP) { reveal(); return; }
      self.loadNext().then(function (ok) { ok ? step() : reveal(); });
    }
    step();
  };

  // A single active instance + one delegated button listener, so re-initialising after an AJAX
  // filter swap (which replaces [data-collection-container] and [data-load-more-wrap]) rebinds to
  // the fresh DOM without stacking duplicate click handlers.
  var activeInstance = null;
  var wiredGlobals = false;

  function setupGlobals() {
    if (wiredGlobals) return;
    wiredGlobals = true;
    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest(SEL.button) : null;
      if (!btn) return;
      e.preventDefault();
      if (activeInstance) activeInstance.loadNext();
    });
  }

  function init() {
    setupGlobals();
    var wrap = document.querySelector(SEL.wrap);
    activeInstance = wrap ? new LoadMore(wrap) : null;
  }

  ready(init);
  window.PatinaLoadMore = { init: init };
})();
